import * as Sentry from "@sentry/browser";
import type { SentryConfig, SentryIntegration, LoggerContext } from "./types";
import type { Logger } from "./logger";

export class SentryLogger implements SentryIntegration {
  private config: SentryConfig;
  private logger: Logger;
  private initialized = false;

  constructor(config: SentryConfig, logger: Logger) {
    this.config = config;
    this.logger = logger;
  }

  init(): void {
    if (this.initialized) {
      this.logger.warn("Sentry already initialized");
      return;
    }

    if (!this.config.dsn) {
      this.logger.warn("Sentry DSN not provided, skipping initialization");
      return;
    }

    try {
      Sentry.init({
        dsn: this.config.dsn,
        environment: this.config.environment || "development",
        release: this.config.release,
        tracesSampleRate: this.config.tracesSampleRate || 0.1,
        replaysSessionSampleRate: this.config.replaysSessionSampleRate || 0.1,
        replaysOnErrorSampleRate: this.config.replaysOnErrorSampleRate || 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        // beforeSend: this.config.beforeSend,
      });

      this.initialized = true;
      this.logger.info("Sentry initialized successfully", {
        environment: this.config.environment,
        dsn: this.config.dsn.replace(/.*@/, "***@"), // Hide sensitive part
      });
    } catch (error) {
      this.logger.error("Failed to initialize Sentry", { error });
    }
  }

  captureException(error: Error, context?: LoggerContext): void {
    if (!this.initialized) {
      this.logger.warn("Sentry not initialized, cannot capture exception", {
        error,
      });
      return;
    }

    const correlationId = context?.correlationId;

    // Set correlation ID as a tag
    if (correlationId) {
      Sentry.setTag("correlationId", correlationId);
    }

    // Set user context if available
    if (context?.userId) {
      Sentry.setUser({ id: context.userId });
    }

    // Add additional context
    if (context) {
      Sentry.setContext("logger", context);
    }

    // Capture the exception
    Sentry.captureException(error, {
      tags: { correlationId },
      contexts: {
        logger: context || {},
      },
    });

    this.logger.error("Exception captured and sent to Sentry", {
      ...context,
      errorMessage: error.message,
      errorStack: error.stack,
    });
  }

  captureMessage(
    message: string,
    level: string,
    context?: LoggerContext,
  ): void {
    if (!this.initialized) {
      this.logger.warn("Sentry not initialized, cannot capture message", {
        message,
      });
      return;
    }

    const correlationId = context?.correlationId;

    // Set correlation ID as a tag
    if (correlationId) {
      Sentry.setTag("correlationId", correlationId);
    }

    // Set user context if available
    if (context?.userId) {
      Sentry.setUser({ id: context.userId });
    }

    // Add additional context
    if (context) {
      Sentry.setContext("logger", context);
    }

    // Capture the message
    Sentry.captureMessage(message, level as Sentry.SeverityLevel);

    this.logger.info(
      `Message captured and sent to Sentry: ${message}`,
      context,
    );
  }

  setContext(key: string, context: Record<string, unknown>): void {
    if (!this.initialized) {
      this.logger.warn("Sentry not initialized, cannot set context", { key });
      return;
    }

    Sentry.setContext(key, context);
  }

  setUser(user: Sentry.User | null): void {
    if (!this.initialized) {
      this.logger.warn("Sentry not initialized, cannot set user", { user });
      return;
    }

    Sentry.setUser(user);
  }

  setTag(key: string, value: string): void {
    if (!this.initialized) {
      this.logger.warn("Sentry not initialized, cannot set tag", {
        key,
        value,
      });
      return;
    }

    Sentry.setTag(key, value);
  }

  addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
    if (!this.initialized) {
      this.logger.warn("Sentry not initialized, cannot add breadcrumb", {
        breadcrumb,
      });
      return;
    }

    Sentry.addBreadcrumb(breadcrumb);
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
